/**
 * Geocoding Backfill Panel
 * ─────────────────────────
 * Operator view for the lat/lng backfill cron defined in
 * /app/backend/routes/geocoding.py.
 *
 * Why a dedicated panel: 391 of the 392 imported sites came in
 * without coordinates. Without lat/lng they don't appear on the live
 * map or in nearest-tech lookups. This panel lets admins:
 *
 *   • See queue health at a glance (geocoded / pending / errors).
 *   • Kick off the backfill, which runs in the background at ~1
 *     req/sec against Nominatim (~7 minutes for 392 sites).
 *   • Cancel a runaway run.
 *   • Review the "skip list" of sites whose addresses Nominatim
 *     couldn't resolve, so an AM can fix the underlying address.
 *
 * Polls /api/geocoding/status every 3s while a run is active so the
 * progress counters tick live.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { tr } from "@/i18n";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MapPin, Play, Square, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { SettingsPanel, PanelHeader, PanelBody, Callout, EmptyState, LoadingState, CollapsibleSection } from "@/components/settings/SettingsUI";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const Stat = ({ label, value, tone = "default", testId }) => {
  // One neutral tile surface; the value carries the tone so the cards
  // read consistently in both light and dark mode.
  const valueTone = {
    default: "text-slate-900",
    good: "text-emerald-600",
    warn: "text-amber-600",
    bad: "text-rose-600",
  }[tone];
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-0.5 ${valueTone}`}>{value}</div>
    </div>
  );
};

export default function GeocodingPanel() {
  const [status, setStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/geocoding/status`, { withCredentials: true });
      setStatus(data);
      return data;
    } catch (e) {
      // Don't toast on every poll failure — only on the first manual load.
      return null;
    }
  }, []);

  const fetchErrors = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/geocoding/errors`, {
        params: { limit: 500 },
        withCredentials: true,
      });
      setErrors(data || []);
    } catch {
      /* surfaced via the count, not blocking */
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchErrors()]);
      setLoading(false);
    })();
  }, [fetchStatus, fetchErrors]);

  // Live-poll while a run is active so the counters tick in real time.
  useEffect(() => {
    if (status?.running) {
      pollRef.current = setInterval(async () => {
        const s = await fetchStatus();
        // If the run just finished, refresh the error list once.
        if (s && !s.running) {
          fetchErrors();
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 3000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
      };
    }
    return undefined;
  }, [status?.running, fetchStatus, fetchErrors]);

  const handleRun = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/geocoding/run`, {}, { withCredentials: true });
      toast.success("Backfill started. Counters will tick as sites are processed.");
      await fetchStatus();
    } catch (e) {
      const msg = e.response?.data?.detail || "Couldn't start backfill";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/geocoding/cancel`, {}, { withCredentials: true });
      toast.message("Cancel requested. Run will stop after the current site.");
      await fetchStatus();
    } catch (e) {
      const msg = e.response?.data?.detail || "Couldn't cancel";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async (siteId) => {
    try {
      await axios.post(`${API}/geocoding/errors/${siteId}/dismiss`, {}, { withCredentials: true });
      toast.success("Cleared from skip-list. Will retry on next run.");
      await Promise.all([fetchStatus(), fetchErrors()]);
    } catch {
      toast.error("Couldn't clear entry");
    }
  };

  const section = (extra, children) => (
    <CollapsibleSection id="geocoding" testId="geocoding-panel" icon={MapPin} iconClass="text-emerald-600"
      title="Geocoding" hint="Site lat/lng backfill against Nominatim" extra={extra}>
      {children}
    </CollapsibleSection>
  );

  if (loading) {
    return section(null, <LoadingState testId="geocoding-loading" />);
  }

  if (!status) {
    return section(null,
      <EmptyState
        icon={AlertTriangle}
        title="Couldn't load geocoding status"
        hint="Check that the backend is running and you have permission."
        testId="geocoding-error-state"
      />
    );
  }

  const pct = status.total_sites > 0
    ? Math.round((status.geocoded / status.total_sites) * 100)
    : 0;

  return section(
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold ${
      status.pending > 0 || status.errors > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
          data-testid="geocoding-state">
      {status.geocoded}/{status.total_sites} geocoded
    </span>,
    <div className="space-y-3">
      <SettingsPanel>
        <PanelHeader
          icon={MapPin}
          title="Geocoding Backfill"
          hint="Site lat/lng backfill against Nominatim"
          actions={!status.enabled && (
            <span className="text-[10px] font-semibold bg-rose-100 text-rose-800 border border-rose-300 px-2 py-0.5 rounded-full">
              DISABLED via env
            </span>
          )}
        />

        <div className="px-5 py-4 space-y-5">
          {/* Stat grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total Sites" value={status.total_sites} testId="geo-stat-total" />
            <Stat label="Geocoded" value={`${status.geocoded} (${pct}%)`} tone="good" testId="geo-stat-geocoded" />
            <Stat label="Pending" value={status.pending} tone={status.pending > 0 ? "warn" : "default"} testId="geo-stat-pending" />
            <Stat label={tr("Needs Review")} value={status.errors} tone={status.errors > 0 ? "bad" : "default"} testId="geo-stat-errors" />
          </div>

          {/* Run / cancel controls */}
          <div className="flex items-center gap-3">
            {!status.running ? (
              <Button
                onClick={handleRun}
                disabled={busy || !status.enabled || status.pending === 0}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="geo-run-btn"
              >
                <Play className="w-4 h-4 mr-1.5" />
                Run backfill
              </Button>
            ) : (
              <Button
                onClick={handleCancel}
                disabled={busy}
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="geo-cancel-btn"
              >
                <Square className="w-4 h-4 mr-1.5" />
                Cancel run
              </Button>
            )}
            <Button
              variant="outline"
              onClick={async () => { await Promise.all([fetchStatus(), fetchErrors()]); }}
              data-testid="geo-refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              {tr("Refresh")}
            </Button>

            {status.running && (
              <span className="text-sm text-slate-600 tabular-nums" data-testid="geo-live-progress">
                Live: processed <b>{status.processed || 0}</b> · geocoded <b className="text-emerald-700">{status.this_run_geocoded || 0}</b> · skipped <b className="text-amber-700">{status.this_run_skipped || 0}</b> · errors <b className="text-rose-700">{status.this_run_errors || 0}</b>
              </span>
            )}
          </div>

          {/* Last run summary */}
          {!status.running && status.finished_at && (
            <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="geo-last-run">
              {status.last_outcome === "completed" ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              )}
              Last run {status.last_outcome} at {fmtTime(status.finished_at)} —
              processed {status.processed || 0}, geocoded {status.this_run_geocoded || 0},
              skipped {status.this_run_skipped || 0}, errors {status.this_run_errors || 0}
            </div>
          )}

          <Callout>
            Runs against Nominatim at ~1 request/second per their published rate limit.
            <b className="ml-1">Note:</b> Tax-authority backfill is <i>not</i> automated — that field still requires manual entry per site.
          </Callout>
        </div>
      </SettingsPanel>

      {/* Skip list */}
      <SettingsPanel testId="geo-errors-section">
        <PanelHeader
          icon={AlertTriangle}
          iconClass="text-amber-600"
          title="Needs manual review"
          count={errors.length}
          countTone={errors.length > 0 ? "warn" : "neutral"}
          hint="Addresses Nominatim couldn't resolve"
        />

        {errors.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="Nothing to review" hint="Every pending address looks geocodable." testId="geo-errors-empty" />
        ) : (
          <div className="divide-y">
            {errors.map((e) => (
              <div key={e.site_id} className="px-5 py-3 flex items-start justify-between gap-3" data-testid={`geo-error-row-${e.site_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{e.site_name}</div>
                  <div className="text-xs text-slate-500 truncate">{e.address || <i>(no address on file)</i>}</div>
                  <div className="text-xs text-rose-700 mt-0.5">{e.reason}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Last tried {fmtTime(e.last_attempt_at)}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDismiss(e.site_id)}
                  data-testid={`geo-error-dismiss-${e.site_id}`}
                >
                  Retry on next run
                </Button>
              </div>
            ))}
          </div>
        )}
      </SettingsPanel>
    </div>
  );
}
